import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FormattedHTMLMessage, FormattedRelativeTime, injectIntl} from 'react-intl';
import {selectUnit} from '@formatjs/intl-utils';
import {INITIALS_SIZE, PROJECT_STATUS} from '../../../../constants';
import {trackNotificationOpened} from '../../../../tracking/amplitude/timeApprovalEvents';
import ActionsMenu from '../action-menu/actions_menu';
import Moment from 'moment';
import Util from '../../util/util';
import {Link} from 'react-router-dom';
import UpdatePersonNotificationMutation from '../../../../mutations/update_person_notification_mutation';
import PersonInitials from '../person/person_initials';
import {profilePicSrc, projectUrl} from '../../../../directApi';
import DeprecatedProjectIndicatorJS from '../project-indicator/js/DeprecatedProjectIndicatorJS';
import {DeprecatedProjectIndicatorComponent} from '../project-indicator/DeprecatedProjectIndicator';
import {getProjectIndicatorString} from '../project-indicator/support/ProjectIndicatorLogic';
import {dateRangeSelectValue} from './notificationDateRange';
import {stageLabel} from '../../util/projectStageUtil';
import {Icon} from '@forecast-it/design-system';
import styled from 'styled-components';

// The two actions a Nova run produces. Kept beside the icon that answers to them so the pair
// cannot drift apart: a third action added to the switch above without being named here would
// silently fall back to the publisher's avatar.
const NOVA_ACTIONS = ['TASK_NOVA_RUN_SUCCEEDED', 'TASK_NOVA_RUN_FAILED'];
const isNovaAction = action => NOVA_ACTIONS.includes(action);

// The hexagon sizes its own contents; this only centres the mark inside it, which an <img> or the
// initials do for themselves.
const NovaNotificationMark = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
`;

function findProject(companyProjectId, projects) {
	if (projects && companyProjectId) {
		return projects.find(project => project.companyProjectId === companyProjectId);
	}
}

// Older notifications store the stage as an enum value ("PLANNING"); newer ones store the stage's name.
function projectStageNotificationLabel(value, intl) {
	return Object.values(PROJECT_STATUS).includes(value)
		? Util.getProjectStageTranslation(value, intl)
		: stageLabel({name: value}, intl);
}

function getNotificationContent(notification, suffix, intl) {
	const params = JSON.parse(notification.params);
	const task = 'T' + params.companyTaskId + ' ' + params.taskName;
	const project = getProjectIndicatorString(params.companyProjectId, params.customProjectId) + ' ' + params.projectName;
	const action = notification.publisherAction;
	const publisher =
		notification.publisher && notification.publisher.node
			? notification.publisher.node.fullName
			: params.publisherName
			? params.publisherName
			: intl.formatMessage({id: 'notifications.a_deleted_user'});
	const oldValue = params.oldValue;
	const newValue = params.newValue;
	const value = params.value;
	const valueAsDate = value => {
		if (value && value.length > 0) {
			const valueArr = value.split('-');
			if (valueArr.length === 3) {
				const yyyy = valueArr[0];
				const mm = valueArr[1];
				const dd = valueArr[2];
				if (yyyy && mm && dd) {
					return Util.CreateNonUtcMomentDate(yyyy, mm, dd)?.format('LL');
				}
			}
		}
		return value;
	};
	switch (action) {
		// TASK UPDATES
		case 'TASK_ESTIMATE_UPDATE': {
			const oldVal =
				suffix !== 'p' ? Util.convertMinutesToFullHour(oldValue, intl) : Math.round((oldValue / 60) * 10) / 10 + suffix;
			const newVal =
				suffix !== 'p' ? Util.convertMinutesToFullHour(newValue, intl) : Math.round((newValue / 60) * 10) / 10 + suffix;

			return {
				id: 'notification.update_task_estimate',
				values: {publisher: publisher, task: task, oldVal, newVal, project: project},
			};
		}
		case 'TASK_UPDATE_TITLE':
			// We need the old value of the title for the task here
			return {
				id: 'notification.update_task_title',
				values: {
					publisher: publisher,
					task: 'T' + params.companyTaskId + ' ' + oldValue,
					oldVal: oldValue,
					newVal: newValue,
				},
				head: 'Title Updated',
			};
		case 'TASK_UPDATE_DESCRIPTION':
			return {
				id: 'notification.update_task_description',
				values: {publisher: publisher, task: task},
				head: 'Description Updated',
			};
		case 'TASK_DESCRIPTION_MENTION':
			return {
				id: 'notification.task_description_mention',
				values: {publisher: publisher, task: task},
				head: 'Mentioned in:',
			};
		case 'SUB_TASK_DESCRIPTION_MENTION':
			return {
				id: 'notification.sub_task_description_mention',
				values: {publisher: publisher, task: task, subTask: params.subTaskName},
				head: 'Mentioned in:',
			};
		case 'PROJECT_DESCRIPTION_MENTION':
			return {
				id: 'notification.project_description_mention',
				values: {publisher: publisher, project: project},
				head: 'Mentioned in:',
			};
		case 'TASK_COMMENT_MENTION':
			return {
				id: 'notification.task_comment_mention',
				values: {publisher: publisher, task: task},
				head: 'Mentioned in:',
			};
		case 'TASK_PERSON_ASSIGNED':
			return {
				id: 'notification.task_person_assigned',
				values: {publisher: publisher, task: task},
				head: 'Assigned to:',
			};
		case 'TASK_PERSON_UNASSIGNED':
			return {
				id: 'notification.task_person_unassigned',
				values: {publisher: publisher, task: task},
				head: 'Unassigned from:',
			};
		// An agent's run finishing on a task. These reuse the two messages the client-side Nova
		// notification list already used, so the copy a person sees does not change now that the
		// entries are persisted and arrive through this component instead.
		case 'TASK_NOVA_RUN_SUCCEEDED':
			return {
				id: 'nova_invocation.notifications.succeeded',
				values: {taskReference: 'T' + params.companyTaskId + ' ', taskName: params.taskName},
				head: `Nova: ${params.agentName || 'Nova'}`,
			};
		case 'TASK_NOVA_RUN_FAILED':
			return {
				id: 'nova_invocation.notifications.failed',
				values: {taskReference: 'T' + params.companyTaskId + ' ', taskName: params.taskName},
				head: `Nova: ${params.agentName || 'Nova'}`,
			};
		case 'TASK_CREATE':
			return {
				id: 'notification.task_create',
				values: {publisher: publisher, task: task},
				head: 'Created:',
			};
		case 'TASK_CHANGE_SCOPE_GROUP':
			return {
				id: 'notification.task_phase_update',
				values: {
					publisher: publisher,
					task: task,
					value: params.newPhaseName ? params.newPhaseName : 'no-phase',
				},
				head: `Status: ${params.newPhaseName ? params.newPhaseName : 'no-phase'}`,
			};
		case 'TASK_CHANGE_STATUS_COLUMN':
			return {
				id: 'notification.task_status_update',
				values: {publisher: publisher, task: task, value: params.newStatusColumnName},
				head: `Status: ${params.newStatusColumnName}`,
			};
		case 'TASK_CHANGE_SPRINT':
			return {
				id: 'notification.task_sprint_update',
				values: {
					publisher: publisher,
					task: task,
					value: params.newSprintName ? params.newSprintName : 'backlog',
				},
				head: `Added to: ${params.newSprintName ? params.newSprintName : 'backlog'}`,
			};
		case 'TASK_REPEAT_REMOVE':
			return {
				id: 'notification.task_repeat_remove',
				values: {publisher: publisher, task: task},
				head: 'Repeat Task: Stop',
			};
		case 'TASK_REPEAT_ADD':
			return {
				id: 'notification.task_repeat_add',
				values: {publisher: publisher, task: task},
				head: 'Repeat Task: Start',
			};
		case 'TASK_TODO_OR_SUB_TASK_RENAME':
		case 'TASK_TODO_OR_SUB_TASK_UPDATE_RENAME':
		case 'TASK_TODO_OR_SUB_TASK_UPDATE_STATE': {
			if (Number(params.projectTaskLevels) === 2) {
				return {
					id: 'notification.task_sub_task_update_state',
					values: {publisher: publisher, task: task, value: value},
				};
			} else
				return {
					id: 'notification.task_todo_update_state',
					values: {publisher: publisher, task: task, value: value},
				};
		}
		case 'TASK_TODO_OR_SUB_TASK_UPDATE_ESTIMATE':
			if (Number(params.projectTaskLevels) === 2) {
				return {
					id: 'notification.task_sub_task_update_estimate',
					values: {publisher: publisher, task: task, value: value},
				};
			} else
				return {
					id: 'notification.task_todo_update_estimate',
					values: {publisher: publisher, task: task, value: value},
				};
		case 'TASK_TODO_OR_SUB_TASK_ADD':
			if (Number(params.projectTaskLevels) === 2) {
				return {
					id: 'notification.task_subtask_add',
					values: {publisher: publisher, task: task, value: value},
					head: 'Subtask: Added',
				};
			} else
				return {
					id: 'notification.task_todo_add',
					values: {publisher: publisher, task: task, value: value},
					head: 'Todo: Added',
				};
		case 'PROJECT_CHANGE':
			return {
				id: 'notification.task_project_change',
				values: {
					publisher: publisher,
					task: task,
					oldProject: 'P' + params.oldCompanyProjectId + ' ' + params.oldProjectName,
					newProject: 'P' + params.newCompanyProjectId + ' ' + params.newProjectName,
				},
				head: 'Moved to:',
			};
		case 'TASK_DEADLINE_REMOVE':
		case 'TASK_DEADLINE_UPDATE':
		case 'TASK_DEADLINE_ADD':
			return {
				id: 'notification.task_deadline_add',
				values: {publisher: publisher, task: task, value: valueAsDate(newValue)},
			};
		case 'TASK_START_DATE_UPDATE':
		case 'TASK_START_DATE_REMOVE':
		case 'TASK_START_DATE_ADD':
			return {
				id: 'notification.task_start_date_add',
				values: {publisher: publisher, task: task, value: valueAsDate(newValue)},
				head: 'Start date: Updated',
			};
		case 'TASK_FILE_ADD':
			return {
				id: 'notification.task_file_add',
				values: {publisher: publisher, task: task, fileName: params.fileName},
				head: 'File: Added',
			};
		case 'TASK_COMMENT_UPDATE':
			return {
				id: 'notification.task_comment_update',
				values: {publisher: publisher, task: task},
				head: 'Comment: Updated',
			};
		case 'TASK_COMMENT_ADD':
			return {
				id: 'notification.task_comment_add',
				values: {publisher: publisher, task: task},
				head: 'Comment: Added',
			};
		case 'TASK_BLOCKED_MARK':
			return {
				id: 'notification.task_blocked_mark',
				values: {publisher: publisher, task: task},
				head: 'Marked as: Blocked',
			};
		case 'TASK_BLOCKED_UNMARK':
			return {
				id: 'notification.task_blocked_unmark',
				values: {publisher: publisher, task: task},
				head: 'Marked as: Unblocked',
			};
		case 'TASK_BUG_MARK':
			return {
				id: 'notification.task_bug_mark',
				values: {publisher: publisher, task: task},
				head: 'Marked as: Bug',
			};
		case 'TASK_BUG_UNMARK':
			return {
				id: 'notification.task_bug_unmark',
				values: {publisher: publisher, task: task},
				head: 'Marked as: None',
			};
		case 'TASK_REPORTED_TIME_ADD': {
			// In the future, this should never be used
			const val = Util.convertMinutesToFullHour(params.minutesRegistered, intl);
			return {
				id: 'notification.task_reported_time_add',
				values: {publisher: publisher, task: task, value: val},
				head: `Time registered: ${publisher}`,
			};
		}
		// PROJECT UPDATES
		case 'PROJECT_UPDATE_STATUS':
			return {
				id: 'notification.project_status_update',
				values: {
					publisher: publisher,
					project: project,
					oldVal: projectStageNotificationLabel(oldValue, intl),
					newVal: projectStageNotificationLabel(newValue, intl),
				},
				head: `Status: ${newValue}`,
			};
		case 'PROJECT_GROUP_ASSIGN_PERSON':
		case 'PROJECT_ASSIGN_PERSON':
			return {
				id: 'notification.project_assign_person',
				values: {publisher: publisher, project: project},
				head: 'Assigned to:',
			};
		// DEADLINES
		case 'TASK_UPCOMING_DEADLINE':
			return {
				id: 'notification.task_upcoming_deadline',
				values: {publisher: publisher, task: task, project: project, numDays: params.numDays},
				head: `Due in ${params.numDays} days`,
			};
		case 'PROJECT_UPCOMING_DEADLINE':
			return {
				id: 'notification.project_upcoming_deadline',
				values: {publisher: publisher, project: project, numDays: params.numDays},
				head: `Due in ${params.numDays} days`,
			};
		// PERSON
		case 'NEW_PERSON_JOINED':
			return {
				id: 'notification.person_join',
				values: {publisher: publisher},
				head: 'New starter',
			};
		case 'INTEGRATION_WARNING':
			return {
				id: 'notification.time_reg_sync_warning',
				values: {date: params.date, publisher: publisher, system: params.system, task: task},
			};
		case 'TIME_OFF_REGISTER':
			return {
				id: 'notification.time_off_registered',
				values: {date: params.date, publisher: publisher, dateRange: params.dateRange},
				head: 'Time off: Registered',
			};
		case 'TIME_OFF_SUBMIT':
			return {
				id: 'notification.time_off_submitted',
				values: {date: params.date, publisher: publisher, dateRange: params.dateRange},
				head: 'Time off: Submitted',
			};
		case 'TIME_OFF_APPROVE':
			return {
				id: 'notification.time_off_approved',
				values: {date: params.date, publisher: publisher, dateRange: params.dateRange},
				head: 'Time off: Approved',
			};
		case 'TIME_OFF_REJECT':
			return {
				id: 'notification.time_off_rejected',
				values: {date: params.date, publisher: publisher, dateRange: params.dateRange},
				head: 'Time off: Rejected',
			};
		case 'TIME_OFF_ALLOCATION_CREATE':
			return {
				id: 'notification.time_off_allocation_created',
				values: {date: params.date, publisher: publisher, dateRange: params.dateRange},
			};
		case 'TIME_OFF_ALLOCATION_UPDATE_DATE':
			return {
				id: 'notification.time_off_allocation_date_updated',
				values: {
					date: params.date,
					publisher: publisher,
					dateRange: params.dateRange,
					oldDateRange: params.oldDateRange,
				},
				head: 'Time off: Updated',
			};
		case 'TIME_OFF_ALLOCATION_UPDATE_TYPE':
			return {
				id: 'notification.time_off_allocation_type_updated',
				values: {
					date: params.date,
					publisher: publisher,
					dateRange: params.dateRange,
					oldType: params.oldType,
					newType: params.newType,
				},
				head: 'Time off: Updated',
			};
		case 'TIME_OFF_ALLOCATION_UPDATE_HOURS':
			return {
				id: 'notification.time_off_allocation_hours_updated',
				values: {
					date: params.date,
					publisher: publisher,
					dateRange: params.dateRange,
					oldHours: params.oldHours,
					newHours: params.newHours,
				},
				head: 'Time off: Updated',
			};
		case 'TIME_OFF_ALLOCATION_DELETE':
			return {
				id: 'notification.time_off_allocation_deleted',
				values: {date: params.date, publisher: publisher, dateRange: params.dateRange},
				head: 'Time off: Deleted',
			};
		// TIMESHEET APPROVAL
		case 'TIMESHEET_SUBMIT':
			return {
				id: 'notification.timesheet_submitted',
				values: {publisher, dateRange: dateRangeSelectValue(params.dateRange)},
				head: 'Timesheet: Submitted',
			};
		case 'TIMESHEET_APPROVE':
			return {
				id: 'notification.timesheet_approved',
				values: {publisher, dateRange: dateRangeSelectValue(params.dateRange)},
				head: 'Timesheet: Approved',
			};
		case 'TIMESHEET_DECLINE':
			return {
				id: 'notification.timesheet_declined',
				values: {
					publisher,
					dateRange: dateRangeSelectValue(params.dateRange),
					reason: params.reason
						? params.reason.length > 80
							? params.reason.slice(0, 80) + '...'
							: params.reason
						: 'empty',
				},
				head: 'Timesheet: Declined',
			};
		case 'TIMESHEET_REVERSE_APPROVAL':
			return {
				id: 'notification.timesheet_approval_reversed',
				values: {
					publisher,
					dateRange: dateRangeSelectValue(params.dateRange),
					reason: params.reason
						? params.reason.length > 80
							? params.reason.slice(0, 80) + '...'
							: params.reason
						: 'empty',
				},
				head: 'Timesheet: Approval Reversed',
			};
		case 'TIMESHEET_RESUBMIT':
			return {
				id: 'notification.timesheet_resubmitted',
				values: {publisher, dateRange: dateRangeSelectValue(params.dateRange)},
				head: 'Timesheet: Resubmitted',
			};
		case 'TIMESHEET_INVOICE':
			return {
				id: 'notification.timesheet_invoiced',
				values: {publisher, dateRange: dateRangeSelectValue(params.dateRange)},
				head: 'Timesheet: Invoiced',
			};
		case 'INVOICE_CREATED':
			return {
				id: 'notification.invoice_created',
				values: {project: params.project, invoiceName: params.invoiceName, publisher: publisher},
				head: 'Invoice: Created',
			};
		case 'INVOICE_STATUS_UPDATED':
			return {
				id: 'notification.invoice_status_updated',
				values: {
					status: params.status,
					project: params.project,
					invoiceName: params.invoiceName,
				},
				head: 'Invoice: Updated',
			};
		case 'INVOICE_DATE_UPDATED':
			return {
				id: 'notification.invoice_date_updated',
				values: {
					invoiceDate: params.invoiceDate,
					oldInvoiceDate: params.oldInvoiceDate,
					invoiceName: params.invoiceName,
					publisher: publisher,
				},
				head: 'Invoice: Updated',
			};
		case 'INVOICE_DUE_DATE_UPDATED':
			return {
				id: 'notification.invoice_due_date_updated',
				values: {
					dueDate: params.dueDate,
					oldDueDate: params.oldDueDate,
					invoiceName: params.invoiceName,
					publisher: publisher,
				},
				head: 'Invoice: Updated',
			};
		case 'INVOICE_DELETED':
			return {
				id: 'notification.invoice_deleted',
				values: {project: params.project, invoiceName: params.invoiceName, publisher: publisher},
				head: 'Invoice: Deleted',
			};
		case 'INVOICE_PAYMENT_CREATED':
			return {
				id: 'notification.invoice_payment_created',
				values: {project: params.project, invoiceName: params.invoiceName, amount: params.amount},
				head: 'Invoice: Payment',
			};
		case 'INVOICE_DUE':
			return {
				id: 'notification.invoice_due',
				values: {project: params.project, invoiceName: params.invoiceName},
				head: 'Invoice: Due',
			};
		case 'INVOICE_DATE_REACHED':
			return {
				id: 'notification.invoice_date_reached',
				values: {project: params.project, invoiceName: params.invoiceName},
				head: 'Invoice: Date Reached',
			};
		case 'INVOICE_DAYS_OVERDUE':
			return {
				id: 'notification.invoice_days_overdue',
				values: {project: params.project, invoiceName: params.invoiceName, days: params.dueDays},
				head: 'Invoice: Days Overdue',
			};
		case 'INVOICE_OVERDUE':
			return {
				id: 'notification.invoice_overdue',
				values: {project: params.project, invoiceName: params.invoiceName},
				head: 'Invoice: Overdue',
			};
		// TICKETING (ACC-533) — merged from the standalone ticketing bell.
		// ACC-553: when the payload carries the actor name + ticket subject, render
		// detailed copy; otherwise fall back to the generic strings (older rows
		// created before payload enrichment have neither).
		case 'TICKET_ASSIGNED':
			return params.actorName && params.ticketSubject
				? {
						id: 'notification.ticket_assigned_detailed',
						values: {actorName: params.actorName, ticketSubject: params.ticketSubject},
						head: 'Ticket: Assigned',
				  }
				: {id: 'notification.ticket_assigned', values: {}, head: 'Ticket: Assigned'};
		case 'TICKET_MENTIONED': {
			// Watcher notifications reuse the "mentioned" type but carry a watchedEvent
			// discriminator — render the matching watcher copy when present.
			if (params.watchedEvent && params.actorName && params.ticketSubject) {
				const watchMessageId = {
					status_change: 'notification.ticket_watch_status_detailed',
					priority_change: 'notification.ticket_watch_priority_detailed',
					assignment: 'notification.ticket_watch_assignment_detailed',
					comment_added: 'notification.ticket_watch_comment_detailed',
				}[params.watchedEvent];
				if (watchMessageId) {
					return {
						id: watchMessageId,
						values: {actorName: params.actorName, ticketSubject: params.ticketSubject},
						head: 'Ticket: Update',
					};
				}
			}
			return params.actorName && params.ticketSubject
				? {
						id: 'notification.ticket_mentioned_detailed',
						values: {actorName: params.actorName, ticketSubject: params.ticketSubject},
						head: 'Ticket: Mentioned',
				  }
				: {id: 'notification.ticket_mentioned', values: {}, head: 'Ticket: Mentioned'};
		}
		case 'TICKET_CUSTOMER_REPLIED':
			return params.ticketSubject
				? {
						id: 'notification.ticket_customer_replied_detailed',
						values: {ticketSubject: params.ticketSubject},
						head: 'Ticket: Customer replied',
				  }
				: {id: 'notification.ticket_customer_replied', values: {}, head: 'Ticket: Customer replied'};
		case 'TICKET_SLA_BREACHED': {
			// Pick copy by which SLA category breached; each has a detailed (subject
			// named) and a generic (subject-less) variant. Fall back to the generic
			// string when the ticket subject is missing (older rows), matching the
			// other ticket types rather than rendering a dangling "on ".
			const slaCopy = {
				response: {
					detailed: 'notification.ticket_sla_breached_response',
					generic: 'notification.ticket_sla_breached_response_generic',
				},
				resolution: {
					detailed: 'notification.ticket_sla_breached_resolution',
					generic: 'notification.ticket_sla_breached_resolution_generic',
				},
			}[params.slaType] ?? {
				detailed: 'notification.ticket_sla_breached',
				generic: 'notification.ticket_sla_breached_generic',
			};
			return params.ticketSubject
				? {
						id: slaCopy.detailed,
						values: {ticketSubject: params.ticketSubject},
						head: 'Ticket: SLA breached',
				  }
				: {id: slaCopy.generic, values: {}, head: 'Ticket: SLA breached'};
		}
		default:
			return {
				id: 'notification.default_update_message',
				values: {
					publisher: publisher,
					action: action,
					targetObject: params.companyTaskId != null ? task : project,
				},
			};
	}
}

class Notification extends Component {
	constructor(props) {
		super(props);
		this.state = {
			showContextMenu: false,
			contextMenuPosition: {},
		};
		this.markAsUnread = this.markAsUnread.bind(this);
		this.markAsRead = this.markAsRead.bind(this);
	}

	componentDidUpdate(prevProps, prevState) {
		if (!prevProps.shouldTakeFocus && this.props.shouldTakeFocus) {
			if (this._notification) {
				this._notification.focus();
			}
		}
	}

	getNavigationString(project) {
		const notification = this.props.notification;
		const params = JSON.parse(this.props.notification.params);
		const hasSlash = window.location.href.endsWith('/');
		const hasHash = window.location.href.includes('#');

		if (this.props.notification.entityType === 'TIME_OFF' && notification.publisherAction === 'TIME_OFF_SUBMIT') {
			return '/resourcing/timesheet-approval';
		} else if (this.props.notification.entityType === 'TIME_OFF' && notification.publisherAction === 'TIME_OFF_REGISTER') {
			return '/resourcing/timesheet-entries';
		} else if (this.props.notification.entityType === 'TIME_OFF') {
			return '/timesheets';
		} else if (this.props.notification.entityType === 'PROJECT') {
			return projectUrl(params.companyProjectId, project?.customProjectId) + '/workflow';
		} else if (this.props.notification.entityType === 'PERSON') {
			return '/admin/people';
		} else if (this.props.notification.entityType === 'TICKET') {
			// Navigate by the Ticket *global id* (not the decoded local id): the
			// /tickets/:id "by id" route hands the param to useTicketDetailQuery, whose
			// resolver runs fromGlobalId() on it. A bare local id decodes to an empty id
			// there, producing `internal/tickets//detail` -> 404. Matches the parent-ticket
			// link in TicketMetaPanel, which also routes by global id.
			return `/tickets/${params.ticketGlobalId}`;
		} else if (this.props.notification.entityType === 'INVOICE') {
			if (params.companyProjectId) {
				if (params.companyInvoiceId) {
					return `${projectUrl(params.companyProjectId, project?.customProjectId)}/financials/I-${
						params.companyInvoiceId
					}#invoicing`;
				} else {
					return `${projectUrl(params.companyProjectId, project?.customProjectId)}/financials#invoicing`;
				}
			} else {
				if (params.companyInvoiceId) {
					return `/finance/invoices/I-${params.companyInvoiceId}`;
				} else {
					return '/finance/invoices';
				}
			}
		} else if (this.props.notification.entityType === 'TIMESHEET') {
			if (notification.publisherAction === 'TIMESHEET_SUBMIT' || notification.publisherAction === 'TIMESHEET_RESUBMIT') {
				return '/resourcing/timesheet-approval';
			} else {
				return '/timesheets';
			}
		}

		let windowLocation = window.location.pathname;

		if (hasHash) {
			let locationArr = windowLocation.split('#');
			windowLocation = locationArr[0];
		}
		return windowLocation + (hasSlash ? 'T' : '/T') + params.companyTaskId;
	}

	handleNav() {
		// Timesheet approval notifications only. `publisherAction` is passed verbatim: the bare
		// SUBMIT / APPROVE / DECLINE spellings are not values that exist anywhere in the code.
		const {publisherAction} = this.props.notification;
		if (typeof publisherAction === 'string' && publisherAction.startsWith('TIMESHEET_')) {
			trackNotificationOpened(publisherAction);
		}

		this.markAsRead();
		if (this.props.closeNotifications) {
			this.props.closeNotifications();
		}
	}

	markAsRead(e) {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		if (this.props.notification.isTicketNotification) {
			if (this.props.markTicketNotificationRead) {
				this.props.markTicketNotificationRead(this.props.notification.id);
			}
		} else {
			Util.CommitMutation(UpdatePersonNotificationMutation, {
				ids: [this.props.notification.id],
				read: true,
			});
		}
		if (this.state.showContextMenu) {
			this.closeContextMenu();
		}
	}

	markAsUnread(e) {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		Util.CommitMutation(UpdatePersonNotificationMutation, {
			ids: [this.props.notification.id],
			read: false,
		});
		if (this.state.showContextMenu) {
			this.closeContextMenu();
		}
	}

	disableNotificationType(e, type) {
		if (e) {
			e.stopPropagation();
			e.preventDefault();
		}
		this.props.disableNotificationType(type);
		if (this.state.showContextMenu) {
			this.closeContextMenu();
		}
	}

	getActionMenuOptions() {
		const {notification} = this.props;
		const {formatMessage} = this.props.intl;
		// Ticket notifications (ACC-533) only support mark-read on the backend —
		// no mark-unread, no disable-type — so offer just that option.
		if (notification.isTicketNotification) {
			if (notification.read) {
				return [];
			}
			return [{text: formatMessage({id: 'notifications.mark_as_read'}), onClick: this.markAsRead}];
		}
		const opts = [];
		const notificationFlag = Util.getNotificationFlagFromType(notification.publisherAction);
		const isNotifyEnabled = this.props.viewer[notificationFlag];
		// Mark as read/unread
		if (notification.read) {
			opts.push({text: formatMessage({id: `notifications.mark_as_unread`}), onClick: this.markAsUnread});
		} else {
			opts.push({text: formatMessage({id: `notifications.mark_as_read`}), onClick: this.markAsRead});
		}
		// Disable this type of notification
		opts.push({
			text: isNotifyEnabled
				? formatMessage(
						{id: 'notifications.disable_notification_type'},
						{type: Util.prettifyNotificationType(notification.publisherAction, this.props.intl)}
				  )
				: formatMessage(
						{id: 'notifications.enable_notification_type'},
						{type: Util.prettifyNotificationType(notification.publisherAction, this.props.intl)}
				  ),
			onClick: e => this.disableNotificationType(e, notification.publisherAction),
		});
		return opts;
	}

	closeContextMenu() {
		this.setState({
			showContextMenu: false,
		});
	}

	showContextMenu(e) {
		e.preventDefault();
		const contextMenuPosition = {};
		contextMenuPosition.x = e.clientX;
		//check if there is place for menu underneath cursor
		if (window.innerHeight - e.pageY < 250) {
			contextMenuPosition.y = e.pageY - 250;
		} else {
			contextMenuPosition.y = e.pageY;
		}
		this.setState({showContextMenu: true, contextMenuPosition});
	}

	stripHtml(string) {
		return string.replace(/<b>|<\/b>/g, '');
	}

	render() {
		const {notification, projects} = this.props;
		const publisher = notification.publisher;
		const action = notification.publisherAction;
		const params = JSON.parse(notification.params);
		const actionMenuOptions = this.getActionMenuOptions();
		const suffix = params.estimationUnit ? params.estimationUnit[0].toLowerCase() : '';
		const notificationContent = getNotificationContent(notification, suffix, this.props.intl);
		const profileSrc = profilePicSrc(publisher?.node?.profilePictureId);
		const createdAt = Moment.utc(Number(notification.createdAt));
		const project = findProject(params.companyProjectId, projects);
		const {value, unit} = selectUnit(createdAt);
		return (
			<Link
				onBlur={this.props.onBlur}
				onKeyDown={this.props.onKeyDown}
				onClick={this.handleNav.bind(this)}
				className={'notification wrapper react-router-link' + (this.props.isLast ? ' last' : '')}
				to={this.getNavigationString(project)}
			>
				<div
					className={'notification' + (!notification.read ? ' unread' : '')}
					title={this.stripHtml(
						this.props.intl.formatMessage({id: notificationContent.id}, notificationContent.values)
					)}
				>
					<div className="notification-icon">
						<div className="hexagon">
							{isNovaAction(action) ? (
								// The run's own mark, not the avatar of whoever started it. A Nova
								// notification is Nova reporting back, and the person who pressed Action is
								// the one being told - their own face in the slot read as if they had done
								// something to themselves. Everything else in the row is the panel's own.
								<NovaNotificationMark>
									<Icon icon="sparkle" size="s" color="primary" aria-hidden="true" />
								</NovaNotificationMark>
							) : notification.isTicketNotification && !publisher ? (
								// ACC-538: ticket notification with no resolved actor (e.g.
								// customer_replied, or actor not in the company) — keep the glyph.
								<svg
									className={'ticket-icon'}
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={2}
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" />
									<path d="M13 6v12" strokeDasharray="2 2" />
								</svg>
							) : action.includes('UPCOMING_DEADLINE') ? (
								<div className={'deadline-icon'} />
							) : profileSrc ? (
								<img
									crossOrigin="use-credentials"
									data-private
									alt=""
									src={profileSrc}
									height="34"
									width="34"
								/>
							) : publisher && publisher.node && publisher.node.initials ? (
								<PersonInitials initials={publisher.node.initials} size={INITIALS_SIZE.LARGE} />
							) : notification.isTicketNotification ? (
								// ACC-538: ticket notification whose actor resolved but has neither
								// a picture nor initials — fall back to the glyph rather than an
								// empty hexagon.
								<svg
									className={'ticket-icon'}
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={2}
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" />
									<path d="M13 6v12" strokeDasharray="2 2" />
								</svg>
							) : null}
						</div>
					</div>
					<div className="notification-text-wrapper">
						<div className={'notification-text' + (this.props.fullscreenView ? ' full' : '')}>
							<FormattedHTMLMessage id={notificationContent.id} values={notificationContent.values} />
						</div>
						<div className={'bottom-row'}>
							{notification.isTicketNotification ? (
								<div className={'ticket-pill'}>
									{this.props.intl.formatMessage({id: 'notification.ticket_pill'})}
								</div>
							) : project ? (
								<DeprecatedProjectIndicatorJS project={project} disableLink />
							) : params.companyProjectId ? (
								<DeprecatedProjectIndicatorComponent
									customProjectId={params.customProjectId}
									companyProjectId={params.companyProjectId}
									projectColor={params.projectColor}
								/>
							) : null}
							<div className={'indicators'}>
								{action.includes('SUB') || action.includes('TODO') ? <div className={'sub-task-icon'} /> : null}
								{action.includes('COMMENT') ? <div className={'comment-icon'} /> : null}
								{action.includes('FILE') ? <div className={'file-icon'} /> : null}
								{action.includes('HIGH_PRIORITY') ? <div className={'high-priority-icon'} /> : null}
								{action.includes('BUG') ? <div className={'bug-icon'} /> : null}
								{action.includes('BLOCKED') ? <div className={'blocked-icon'} /> : null}
							</div>
							<div className={'timestamp'} title={createdAt.format('LL')}>
								{unit === 'month' || unit === 'year' ? (
									createdAt.format('LL')
								) : (
									<FormattedRelativeTime
										numeric="auto"
										value={unit === 'second' && value > -10 ? 0 : value}
										unit={unit}
									/>
								)}
							</div>
						</div>
					</div>
					<div className="notification-dot-menu">
						<ActionsMenu useGreyIcon={true} isWhite={true} whiteInner={true} options={actionMenuOptions} />
					</div>
				</div>
			</Link>
		);
	}
}

Notification.propTypes = {
	notification: PropTypes.object.isRequired,
	onBlur: PropTypes.func.isRequired,
	onKeyDown: PropTypes.func.isRequired,
};

export default injectIntl(Notification);
